<template>
  <Card class="w-full">
    <div v-if="showNotAFitHelper || showClosedJobHelper" class="mb-8">
      <HelperText
        v-if="showNotAFitHelper"
        data-testid="provider-application-not-a-fit-helper"
        text="Family marked application as not a fit"
        :status="helperStatus.Neutral"
      >
        <template #customIcon>
          <WarningOutlineIcon :width="16" :height="16" color="#a87b00" />
        </template>
      </HelperText>

      <HelperText
        v-else-if="showClosedJobHelper"
        data-testid="provider-application-job-closed-helper"
        text="Job closed"
        :status="helperStatus.Neutral"
      >
        <template #customIcon>
          <BanIcon :width="16" :height="16" color="#8a8a8f" />
        </template>
      </HelperText>
    </div>

    <h3 class="job-text text-h5" :class="{ '!text-grey-text-subdued': !isActive }">
      {{ jobTitle }}
    </h3>

    <div class="mt-16" data-testid="provider-application-schedule">
      <JobSchedule abbreviated-schedule :job="jobForJobSchedule" :display-as-active="displayAsActive" />
    </div>
    <p class="mt-16 underline">
      <router-link :to="jobDetailsRoute">View full job details</router-link>
    </p>
    <div class="flex w-full mt-16">
      <router-link
        v-if="showMessageButton"
        :to="messagesRoute"
        class="relative inline-flex flex-col items-center"
        data-testid="provider-application-messages-link"
        aria-label="View conversation"
      >
        <div class="relative pt-8">
          <Badge v-if="unreadMessageCount > 0" :badge-content="unreadMessageCount" class="absolute top-0 left-16" />
          <MessageSentOutlineIcon :width="24" :height="24" color="#4B4C52" />
        </div>
        <p class="text-caption mt-6" data-testid="provider-application-messages-label">Messages</p>
      </router-link>
      <p
        v-else-if="appliedDate"
        class="job-text text-body-2 text-grey-text-subdued"
        data-testid="provider-application-applied-date"
      >
        Applied on {{ appliedDate }}
      </p>
      <p class="grow"></p>
      <div v-if="showParentViewedHelper">
        <HelperText
          data-testid="provider-application-parent-viewed-helper"
          text="Application viewed"
          :status="helperStatus.Valid"
          class="!mt-0"
        />
      </div>
    </div>
  </Card>
</template>

<script lang="ts">
import { format, parseISO } from 'date-fns';
import { defineComponent } from 'vue';
import Card from '@/components/card/Card.vue';
import Badge from '@/components/badge/Badge.vue';
import HelperText from '@/components/helper_text/HelperText.vue';
import JobSchedule from '@/components/schedule/JobSchedule.vue';
import helperStatus from '@/helpers/status';
import BanIcon from '@/graphics/icons/BanIcon.vue';
import MessageSentOutlineIcon from '@/graphics/icons/MessageSentOutlineIcon.vue';
import WarningOutlineIcon from '@/graphics/icons/WarningOutlineIcon.vue';
import { JobDetailsPageSource } from '@/apps/provider/jobs/details/jobDetailsPageSource';

export default defineComponent({
  name: 'ProviderApplicationCard',
  components: { Badge, BanIcon, Card, HelperText, JobSchedule, MessageSentOutlineIcon, WarningOutlineIcon },
  props: {
    application: {
      type: Object,
      required: true,
    },
    /**
     * When `true` or `false`, drives active styling instead of `job.isActive`. Omit to use `job.isActive`.
     * `type` + `default: undefined` keeps an omitted prop as `undefined` (Vue 2 would otherwise coerce Boolean to `false`).
     */
    displayAsActive: {
      type: Boolean,
      required: false,
      default: undefined,
    },
  },
  data(): { helperStatus: typeof helperStatus } {
    return { helperStatus };
  },
  computed: {
    job(): any {
      return this.application.job;
    },
    /** Spread job plus `isActive` so JobSchedule can grey inactive schedules without a separate `closed` prop. */
    jobForJobSchedule(): Record<string, unknown> {
      return { ...this.job, isActive: this.isActive };
    },
    jobTitle(): string {
      return this.job.title || 'Closed Job';
    },
    jobDetailsRoute(): { name: string; params: { id: string }; query: Record<string, string> } {
      return {
        name: 'JobDetails',
        params: { id: this.job.id },
        query: { source: JobDetailsPageSource.Applications },
      };
    },
    isActive(): boolean {
      if (this.displayAsActive !== undefined) {
        return this.displayAsActive;
      }
      return !!this.job.isActive;
    },
    showNotAFitHelper(): boolean {
      return this.application.parentInterestStatus === 'NOT_A_FIT';
    },
    showClosedJobHelper(): boolean {
      return !this.isActive && !this.showNotAFitHelper;
    },
    appliedDate(): string | null {
      const date = this.application.createdAt;
      if (!date) return null;

      const parsedDate = parseISO(date);
      if (Number.isNaN(parsedDate.getTime())) return null;

      return format(parsedDate, 'MM/dd/yyyy');
    },
    hasParent(): boolean {
      return Boolean(this.job.parent?.id);
    },
    showMessageButton(): boolean {
      return Boolean(this.application.parentLastRepliedAt && this.hasParent);
    },
    showParentViewedHelper(): boolean {
      return this.application.hasParentViewed && !this.showMessageButton;
    },
    unreadMessageCount(): number {
      return this.application.conversation?.unreadMessageCount ?? 0;
    },
    messagesRoute(): { path: string; query: Record<string, string> } | null {
      const parentId = this.job.parent?.id;
      if (!parentId) return null;

      return {
        path: '/messages',
        query: { uuid: parentId },
      };
    },
  },
});
</script>
